/**
 * useHelpChat — the Ask Resida Help conversation: starts a conversation on the first message, streams
 * each reply over SSE, and keeps the thread, draft and limit state. It lives in HelpProvider so the
 * docked panel, the mobile sheet and the /help/chat page share one conversation, and so closing the
 * panel or switching tabs mid-answer loses nothing.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  HELP_STREAM_EVENT_FINAL,
  HELP_STREAM_EVENT_TOKEN,
  HELP_TEXT_ERROR,
  HelpFinalEventSchema,
  HelpTokenEventSchema,
  type EscalationDraft,
  type HelpBotMessage,
  type HelpLimitKind,
  type HelpRating,
} from '@resida/api-types/help';
import {
  createHelpConversation,
  getEscalationDraft,
  getHelpConversation,
  helpKeys,
  isHelpChatDisabled,
  streamHelpMessage,
} from '../../lib/api.help';
import { reportError } from '../../lib/error-reporter';

/** A per-minute limit clears on its own; the longer windows end only when the server says so on the next send. */
const PER_MINUTE_LIMIT_MS = 60_000;

/** idle = nothing in flight; waiting = sent, no token yet (typing indicator); streaming = tokens arriving. */
export type ChatPhase = 'idle' | 'waiting' | 'streaming';

export interface ChatRateLimit {
  /** The server's own limit text, shown verbatim. */
  text: string;
  limitKind?: HelpLimitKind;
}

export interface HelpChat {
  conversationId: string | null;
  messages: HelpBotMessage[];
  /** The answer so far while it streams; empty otherwise. */
  streamingText: string;
  phase: ChatPhase;
  isLoadingConversation: boolean;
  /** Set when a past conversation could not be opened: the conversation to try again. */
  loadError: { conversationId: string } | null;
  draft: string;
  /** Inline field error for a rejected message (over the limit). */
  inputError: string | null;
  /** Set while a usage limit blocks the composer. */
  rateLimit: ChatRateLimit | null;
  /** True once a chat call reported HELP_CHAT_DISABLED (the kill switch is off). */
  disabled: boolean;
  setDraft: (value: string) => void;
  /** Sends the current draft. */
  send: () => Promise<void>;
  /** Resends the last message after an error. */
  retry: () => Promise<void>;
  newConversation: () => void;
  loadConversation: (conversationId: string) => Promise<void>;
  /** Records a rating the server accepted on a message (and whether it now offers escalation). */
  applyRating: (messageId: string, rating: HelpRating, offerEscalation: boolean) => void;
  /** The contact-form draft for the current conversation; null when there is no conversation yet, rejects when the draft cannot be fetched. */
  escalate: () => Promise<EscalationDraft | null>;
}

function localMessage(
  role: HelpBotMessage['role'],
  text: string,
  extra: Partial<HelpBotMessage> = {}
): HelpBotMessage {
  return {
    id: crypto.randomUUID(),
    conversationId: '',
    role,
    text,
    citations: [],
    offerEscalation: false,
    createdAt: new Date().toISOString(),
    ...extra,
  };
}

function parseEvent<T>(
  schema: { safeParse: (value: unknown) => { success: true; data: T } | { success: false } },
  data: string
): T | null {
  try {
    const parsed = schema.safeParse(JSON.parse(data));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function useHelpChat(pathname: string): HelpChat {
  const queryClient = useQueryClient();
  const [conversationId, setConversationIdState] = useState<string | null>(null);
  const [messages, setMessages] = useState<HelpBotMessage[]>([]);
  const [streamingText, setStreamingText] = useState('');
  const [phase, setPhase] = useState<ChatPhase>('idle');
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [loadError, setLoadError] = useState<HelpChat['loadError']>(null);
  const [draft, setDraft] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [rateLimit, setRateLimit] = useState<ChatRateLimit | null>(null);
  const [disabled, setDisabled] = useState(false);

  const conversationIdRef = useRef<string | null>(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  const abortRef = useRef<AbortController | null>(null);

  const setConversationId = useCallback((id: string | null) => {
    conversationIdRef.current = id;
    setConversationIdState(id);
  }, []);

  const abortTurn = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  useEffect(() => abortTurn, [abortTurn]);

  useEffect(() => {
    if (rateLimit?.limitKind !== 'per-minute') return;
    const timer = setTimeout(() => setRateLimit(null), PER_MINUTE_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [rateLimit]);

  const runTurn = useCallback(
    async (text: string) => {
      if (abortRef.current) return;
      const controller = new AbortController();
      abortRef.current = controller;

      const userMessage = localMessage('user', text);
      setInputError(null);
      setLoadError(null);
      setMessages((previous) => [...previous, userMessage]);
      setStreamingText('');
      setPhase('waiting');

      const result: { final: HelpBotMessage | null; text: string; stopped: boolean } = {
        final: null,
        text: '',
        stopped: false,
      };
      try {
        let id = conversationIdRef.current;
        if (!id) {
          id = (await createHelpConversation()).id;
          setConversationId(id);
        }
        await streamHelpMessage(
          id,
          { text, pathname: pathnameRef.current },
          {
            signal: controller.signal,
            onEvent: ({ event, data }) => {
              if (event === HELP_STREAM_EVENT_TOKEN) {
                const token = parseEvent(HelpTokenEventSchema, data);
                if (!token) return;
                result.text += token.text;
                setPhase('streaming');
                setStreamingText(result.text);
              } else if (event === HELP_STREAM_EVENT_FINAL) {
                result.final = parseEvent(HelpFinalEventSchema, data)?.message ?? null;
              }
            },
          }
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        if (isHelpChatDisabled(error)) {
          result.stopped = true;
          setDisabled(true);
          setMessages((previous) => previous.filter((message) => message.id !== userMessage.id));
          setDraft(text);
        } else {
          result.final = localMessage('assistant', HELP_TEXT_ERROR, { outcome: 'error', offerEscalation: true });
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setPhase('idle');
          setStreamingText('');
        }
      }

      if (controller.signal.aborted || result.stopped) return;
      // A stream that ends without its terminal event is an error turn, never a silent stop.
      const final =
        result.final ?? localMessage('assistant', HELP_TEXT_ERROR, { outcome: 'error', offerEscalation: true });
      if (final.outcome === 'rejected-input') {
        setMessages((previous) => previous.filter((message) => message.id !== userMessage.id));
        setDraft(text);
        setInputError(final.text);
      } else if (final.outcome === 'rate-limited') {
        setRateLimit({ text: final.text, limitKind: final.limitKind });
      } else {
        setMessages((previous) => [...previous, final]);
      }
      void queryClient.invalidateQueries({ queryKey: helpKeys.conversations });
    },
    [queryClient, setConversationId]
  );

  const send = useCallback(async () => {
    const text = draft.trim();
    // The composer keeps Send disabled for an empty or over-long draft; the server still rejects one (BR-32).
    if (text === '' || rateLimit) return;
    setDraft('');
    await runTurn(text);
  }, [draft, rateLimit, runTurn]);

  const retry = useCallback(async () => {
    const lastUser = messages.map((message) => message.role).lastIndexOf('user');
    const last = messages[lastUser];
    if (!last) return;
    setMessages(messages.slice(0, lastUser));
    await runTurn(last.text);
  }, [messages, runTurn]);

  const newConversation = useCallback(() => {
    abortTurn();
    setConversationId(null);
    setMessages([]);
    setStreamingText('');
    setPhase('idle');
    setDraft('');
    setInputError(null);
    setLoadError(null);
  }, [abortTurn, setConversationId]);

  const loadConversation = useCallback(
    async (id: string) => {
      abortTurn();
      setPhase('idle');
      setStreamingText('');
      setInputError(null);
      setLoadError(null);
      setIsLoadingConversation(true);
      try {
        const loaded = await getHelpConversation(id);
        setConversationId(id);
        setMessages(loaded.messages);
      } catch (error) {
        if (isHelpChatDisabled(error)) {
          setDisabled(true);
        } else {
          // Not silent: the failure is reported and the thread says the conversation did not open.
          reportError(error, { step: 'help-load-conversation' });
          setLoadError({ conversationId: id });
        }
        setConversationId(null);
        setMessages([]);
      } finally {
        setIsLoadingConversation(false);
      }
    },
    [abortTurn, setConversationId]
  );

  const applyRating = useCallback((messageId: string, rating: HelpRating, offerEscalation: boolean) => {
    setMessages((previous) =>
      previous.map((message) =>
        message.id === messageId
          ? { ...message, rating, offerEscalation: message.offerEscalation || offerEscalation }
          : message
      )
    );
  }, []);

  const escalate = useCallback(async () => {
    const id = conversationIdRef.current;
    return id ? getEscalationDraft(id) : null;
  }, []);

  return {
    conversationId,
    messages,
    streamingText,
    phase,
    isLoadingConversation,
    loadError,
    draft,
    inputError,
    rateLimit,
    disabled,
    setDraft,
    send,
    retry,
    newConversation,
    loadConversation,
    applyRating,
    escalate,
  };
}
